/**
 * Public product page URLs for storefront QR labels.
 * Base URL comes from the store business profile media → social link "website".
 *
 * Pattern (must match storefront slugify):
 *   {website}/product/{slugify(name)}-{storeInventoryItemId}
 */

import {
  extractWebsiteFromProfileMedia,
  fetchBusinessProfileMedia,
} from './profileMediaApi';

export function slugifyProductName(text) {
  return String(text || '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80);
}

export function normalizeProductPublicBaseUrl(value) {
  const text = String(value || '').trim();
  if (!text) return '';
  let url = text.replace(/\/+$/, '');
  if (!/^https?:\/\//i.test(url)) {
    url = `https://${url}`;
  }
  try {
    const parsed = new URL(url);
    return `${parsed.protocol}//${parsed.host}`.replace(/\/+$/, '');
  } catch {
    return url.replace(/\/+$/, '');
  }
}

export function getInventoryItemId(row) {
  if (!row || typeof row !== 'object') return null;
  const id =
    row.inventoryItemId ??
    row.Inventory_Item_Id ??
    row.storeInventoryItemId ??
    row.Store_Inventory_Item_Id;
  if (id == null || id === '') return null;
  const n = Number(id);
  return Number.isFinite(n) ? n : id;
}

export function getInventoryItemName(row) {
  if (!row || typeof row !== 'object') return '';
  return String(row.itemName ?? row.Item_Name ?? '').trim();
}

/**
 * @param {object|null|undefined} row inventory row
 * @param {{ baseUrl: string }} options website from business profile media
 * @returns {string} absolute product details URL, or '' if missing pieces
 */
export function buildProductPublicUrl(row, options = {}) {
  const id = getInventoryItemId(row);
  const name = getInventoryItemName(row);
  const base = normalizeProductPublicBaseUrl(options.baseUrl);
  if (id == null || !name || !base) return '';
  const slug = `${slugifyProductName(name)}-${id}`;
  return `${base}/product/${slug}`;
}

/**
 * Load the store Website from business profile media (DB).
 * @param {string|number} storeId
 * @returns {Promise<string>} normalized base URL or ''
 */
export async function fetchStoreProductPublicBaseUrl(storeId) {
  if (storeId == null || storeId === '') return '';
  const payload = await fetchBusinessProfileMedia('STORE', storeId);
  return normalizeProductPublicBaseUrl(extractWebsiteFromProfileMedia(payload));
}
